import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import {
  buildTransactionTypeFilterOptions,
  TransactionService,
} from '../../../services/transaction.service';
import { UserAlert } from '../../../models/user-alert';
import { AdminTransactionCreatePayload } from '../../../models/transaction';
import { AdminPageHeaderComponent } from '../../shared/admin-page-header/admin-page-header.component';

@Component({
  selector: 'app-transaction-create',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    ProgressSpinnerModule,
    AdminPageHeaderComponent,
  ],
  templateUrl: './transaction-create.component.html',
  styleUrls: ['./transaction-create.component.scss'],
})
export class TransactionCreateComponent implements OnInit {
  transactionForm!: FormGroup;
  filteredUsers: UserAlert[] = [];
  allUsers: UserAlert[] = [];
  isLoadingUsers = false;
  isSubmitting = false;
  initialUsersLoaded = false;
  formSuccess = '';
  formError = '';

  readonly unitOptions = [
    { label: 'Moeda (coin)', value: 'coin' },
    { label: 'Diamante (gem)', value: 'gem' },
  ];

  transactionTypeOptions: { label: string; value: string }[] = [];
  readonly transactionStatusOptions = [
    { label: 'Pendente', value: 'PENDING' },
    { label: 'Aprovado', value: 'APPROVED' },
    { label: 'Reprovado', value: 'FAILED' },
  ];

  constructor(
    private fb: FormBuilder,
    private transactionService: TransactionService,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) {
    this.transactionForm = this.fb.group({
      user: ['', Validators.required],
      unit: ['', Validators.required],
      quantity: [null, [Validators.required, TransactionCreateComponent.integerNonZero]],
      transactionType: ['', Validators.required],
      status: ['', Validators.required],
    });
  }

  private static integerNonZero(
    control: AbstractControl
  ): ValidationErrors | null {
    const raw = control.value;
    if (raw == null || raw === '') {
      return null;
    }
    const value = Number(raw);
    if (!Number.isFinite(value) || !Number.isInteger(value) || value === 0) {
      return { integerNonZero: true };
    }
    return null;
  }

  ngOnInit(): void {
    this.transactionTypeOptions = buildTransactionTypeFilterOptions().filter((o) => o.value !== '');
    this.fetchUsers();
  }

  goBack(): void {
    this.location.back();
  }

  fetchUsers(): void {
    if (this.initialUsersLoaded) return;

    this.isLoadingUsers = true;
    this.formError = '';
    this.transactionService.getUsersTransaction().subscribe({
      next: (response) => {
        this.allUsers = response || [];
        this.filteredUsers = [...this.allUsers];
        this.initialUsersLoaded = true;
        this.isLoadingUsers = false;
      },
      error: () => {
        this.allUsers = [];
        this.filteredUsers = [];
        this.isLoadingUsers = false;
        this.initialUsersLoaded = true;
        this.formError = 'Não foi possível carregar a lista de utilizadores.';
      },
    });
  }

  searchUsers(event: AutoCompleteCompleteEvent): void {
    if (!this.initialUsersLoaded) {
      if (!this.isLoadingUsers) this.fetchUsers();
      this.filteredUsers = [];
      this.cdr.detectChanges();
      return;
    }

    const query = (event.query || '').toLowerCase().trim();
    const filtered = !query
      ? [...this.allUsers]
      : this.allUsers.filter((user) => user.email?.toLowerCase().includes(query));

    this.filteredUsers = filtered;
    this.cdr.detectChanges();
  }

  onUserFieldFocus(): void {
    if (!this.initialUsersLoaded && !this.isLoadingUsers) {
      this.fetchUsers();
    } else if (this.initialUsersLoaded && this.filteredUsers.length === 0) {
      this.filteredUsers = [...this.allUsers];
    }
  }

  confirmTransaction(): void {
    this.transactionForm.markAllAsTouched();
    if (this.transactionForm.invalid) return;

    const formValue = this.transactionForm.value;
    this.isSubmitting = true;
    this.formSuccess = '';
    this.formError = '';

    const body: AdminTransactionCreatePayload = {
      account_id: formValue.user.id,
      unit: formValue.unit,
      quantity: Number(formValue.quantity),
      transaction_type: formValue.transactionType,
      status: formValue.status,
    };

    this.transactionService.createTransaction(body).subscribe({
      next: () => {
        this.clearForm();
        this.isSubmitting = false;
        this.formSuccess = 'Transação criada com sucesso.';
      },
      error: () => {
        this.isSubmitting = false;
        this.formError = 'Não foi possível criar a transação. Tente novamente.';
      },
    });
  }

  clearForm(): void {
    this.transactionForm.reset();
    this.filteredUsers = [...this.allUsers];
    this.initialUsersLoaded = false;
    this.formSuccess = '';
    this.formError = '';
    this.fetchUsers();
  }
}
